import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase, getUserProfile, signOut } from '../lib/supabase';
import { UserChip } from './UserChip';
import { MobileAccountSheet } from './MobileAccountSheet';
import { OnboardingChecklist } from './OnboardingChecklist';
import { applyAccentVars, clearAccentVars } from '../lib/accentColour';
import { normaliseTheme } from '../themes';
import { useBadgeCounts } from '../lib/useBadgeCounts';
import {
  LayoutGrid, Calendar, Users, Scissors, BarChart3,
  CreditCard, LogOut, Sliders, Receipt, ShieldOff, ArrowLeft, Mail, CalendarClock,
  User,
} from 'lucide-react';
import '../styles/instacut.css';

/**
 * AppShell — the locked owner-dashboard chrome.
 * Sidebar nav + main column. Children render their own .topbar + .body.
 * Also gates access: redirects to /signin if no session.
 *
 * IMPERSONATION ("Act as this shop"):
 *   A master_admin can click "Act as this shop" on /admin/shops/:id.
 *   That writes the shop_id into sessionStorage under IMPERSONATION_KEY.
 *   While set, `useShell()` returns a *derived* profile whose:
 *     - role  = 'shop_admin'  (so navFor + gates behave identically to an owner)
 *     - shops = the impersonated shop row
 *     - shop_id = the impersonated shop id
 *     - _impersonating = true
 *   `useShell().realProfile` still exposes the master admin's original
 *   profile for signout/audit purposes. A yellow banner is rendered at the
 *   top of the main column with an [Exit] control.
 */
const IMPERSONATION_KEY = 'instacut_act_as_shop_id';

// Module-scoped cache so that navigating between pages doesn't blank
// the whole shell while we refetch the profile from Supabase. The first
// AppShell mount populates this; every subsequent mount hydrates
// synchronously (no "Loading…" flash) and revalidates in the background.
let cachedProfile = null;
let cachedSessionEmail = '';

export const startActAsShop = (shopId) => {
  if (!shopId) return;
  try { sessionStorage.setItem(IMPERSONATION_KEY, shopId); } catch { /* ignore */ }
};
export const stopActAsShop = () => {
  try { sessionStorage.removeItem(IMPERSONATION_KEY); } catch { /* ignore */ }
};
export const getActAsShopId = () => {
  try { return sessionStorage.getItem(IMPERSONATION_KEY) || null; } catch { return null; }
};

const NAV = [
  { to: '/dashboard',    label: 'Dashboard',   icon: LayoutGrid,  group: 'Shop', roles: ['shop_admin','master_admin','barber'] },
  { to: '/diary',        label: 'Diary',       icon: Calendar,    group: 'Shop', roles: ['shop_admin','master_admin','barber'] },
  { to: '/clients',      label: 'Clients',     icon: Users,       group: 'Shop', roles: ['shop_admin','master_admin','barber'] },
  { to: '/services',     label: 'Services',    icon: Scissors,    group: 'Shop' },
  { to: '/analytics',    label: 'Analytics',   icon: BarChart3,   group: 'Shop' },
  { to: '/settings/schedule', label: 'My schedule', icon: CalendarClock, group: 'Setup', roles: ['barber','shop_admin','master_admin'] },
  { to: '/profile',      label: 'Shop profile',icon: Sliders,     group: 'Setup' },
  { to: '/barbers',      label: 'Barbers',     icon: Users,       group: 'Setup' },
  { to: '/templates',    label: 'Messages',    icon: Mail,        group: 'Setup', roles: ['shop_admin','master_admin'] },
  { to: '/stripe-setup', label: 'Payments',    icon: CreditCard,  group: 'Setup' },
  { to: '/billing',      label: 'Billing',     icon: Receipt,     group: 'Setup' },
];

const navFor = (role) => {
  if (role === 'barber') {
    return NAV.filter(n =>
      n.to === '/dashboard' || n.to === '/diary' || n.to === '/clients' || n.to === '/settings/schedule'
    );
  }
  return NAV.filter(n => !n.roles || n.roles.includes(role));
};

export const AppShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Hydrate from the module cache so cross-page navigations render the
  // sidebar + children immediately. `checking` is only true on the very
  // first mount (before any profile has ever been fetched this session).
  const [profile, setProfile] = useState(cachedProfile);
  const [sessionEmail, setSessionEmail] = useState(cachedSessionEmail);
  const [checking, setChecking] = useState(!cachedProfile);

  // Impersonation ------------------------------------------------------
  const [actAsShopId, setActAsShopId] = useState(getActAsShopId());
  const [actAsShop, setActAsShop] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!supabase) { navigate('/signin'); return; }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/signin'); return; }
      const { data } = await getUserProfile(session.user.id);
      if (mounted) {
        setProfile(data);
        setSessionEmail(session.user.email || '');
        setChecking(false);
        // Populate the module cache so the next page render is instant.
        cachedProfile = data;
        cachedSessionEmail = session.user.email || '';
        // First-login password change — trap the user on /change-password
        // until they set their own password. Master admins are exempt
        // because they never receive an admin-created temp password.
        if (data?.must_change_password && data.role !== 'master_admin'
            && window.location.pathname !== '/change-password') {
          navigate('/change-password');
          return;
        }

        // Frontend role gate. The DB has RLS as the real boundary, but
        // this stops a barber typing an admin URL from ever rendering
        // the page — cleaner UX + belt-and-braces defence.
        const path = window.location.pathname;
        const BARBER_ALLOWED = [
          '/dashboard',
          '/diary',
          '/clients',
          '/change-password',
          '/settings/schedule',
        ];
        const barberAllowed = (p) =>
          BARBER_ALLOWED.some(base => p === base || p.startsWith(base + '/'));

        if (data?.role === 'barber' && !barberAllowed(path)) {
          navigate('/diary');
          return;
        }
        if (data?.role === 'shop_admin' && path.startsWith('/admin')) {
          navigate('/dashboard');
          return;
        }
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only master_admins can impersonate. If somebody else has a stale
  // session value, ignore it and clear on load.
  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'master_admin' && actAsShopId) {
      stopActAsShop();
      setActAsShopId(null);
      setActAsShop(null);
      return;
    }
    if (profile.role === 'master_admin' && actAsShopId) {
      supabase.from('shops').select('*').eq('id', actAsShopId).maybeSingle()
        .then(({ data }) => setActAsShop(data || null));
    } else {
      setActAsShop(null);
    }
  }, [profile, actAsShopId]);

  const exitImpersonation = useCallback(() => {
    stopActAsShop();
    setActAsShopId(null);
    setActAsShop(null);
    navigate('/admin/dashboard');
  }, [navigate]);

  // Cascade the shop's custom accent colour into the admin UI when the
  // Instacut Default theme is picked. Locked themes carry their own
  // palette and shouldn't be overridden by the admin's brand hex.
  const effectiveShop = actAsShop || profile?.shops || null;
  const accentHex = effectiveShop?.accent_colour;
  const themeSlug = normaliseTheme(effectiveShop?.theme);
  useEffect(() => {
    if (themeSlug === 'instacut-default' && accentHex) {
      applyAccentVars(accentHex);
    } else {
      clearAccentVars();
    }
    return () => clearAccentVars();
  }, [themeSlug, accentHex]);

  const handleSignOut = async () => {
    // Wipe the module cache so the next AppShell mount (post sign-in
    // or from a different tab) can't briefly show the previous user.
    cachedProfile = null;
    cachedSessionEmail = '';
    stopActAsShop();
    await signOut();
    navigate('/signin');
  };

  // M2 — toggle `.scrolled-end` on the mobile bottom-nav strip when
  // the user has swiped to the last item, so the right-edge fade
  // affordance disappears once there's nothing more to reveal.
  useEffect(() => {
    const el = document.querySelector('aside.side');
    if (!el) return;
    const onScroll = () => {
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      el.classList.toggle('scrolled-end', atEnd);
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [profile]);

  // Effective profile used by every downstream shop-owner page/hook.
  const effectiveProfile = useMemo(() => {
    if (actAsShop && profile?.role === 'master_admin') {
      return {
        ...profile,
        role: 'shop_admin',              // navFor + entitlement gates
        shop_id: actAsShop.id,
        shops: actAsShop,
        _impersonating: true,
      };
    }
    return profile;
  }, [profile, actAsShop]);

  // Hooks below must run on every render — including the checking-state
  // early-return path — so keep the badge query up here.
  const badges = useBadgeCounts(effectiveProfile);

  if (checking) {
    return (
      <div className="app-root" data-testid="appshell-loading">
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
      </div>
    );
  }

  const groups = ['Shop', 'Setup'];
  const visible = navFor(effectiveProfile?.role);
  const impersonating = !!effectiveProfile?._impersonating;
  const badgeFor = (to) => {
    if (to === '/diary') return badges.diaryTodayRemaining;
    return 0;
  };

  return (
    <div className="app-root">
      <div className="app">
        <aside className="side" data-testid="sidebar">
          <Link to="/dashboard" className="brandrow">
            <img className="mark" src="/logos/instacut-mark-26.svg" alt="" width="26" height="26" />
            Instacut
          </Link>

          {/* Master admins always have a persistent way back to the
              platform console, whether they got here via "Act as this
              shop" or by typing a shop-URL directly. Rendered above the
              nav groups so it's the first thing they see. */}
          {profile?.role === 'master_admin' && (
            <Link
              to="/admin/dashboard"
              onClick={() => { stopActAsShop(); setActAsShopId(null); setActAsShop(null); }}
              className="nav back-to-platform"
              data-testid="nav-back-to-platform"
              style={{
                margin: '4px 8px 10px',
                background: '#0e0f13',
                color: '#fff',
                border: '1px solid #0e0f13',
                borderRadius: 9,
                justifyContent: 'flex-start',
                padding: '9px 12px',
                fontWeight: 600,
              }}
            >
              <ArrowLeft size={15} />
              Back to Platform
            </Link>
          )}

          {groups.map((g) => {
            const items = visible.filter(n => n.group === g);
            if (!items.length) return null;
            return (
              <React.Fragment key={g}>
                <div className="navg">{g}</div>
                {items.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname === item.to ||
                    (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
                  const badge = badgeFor(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`nav ${active ? 'on' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="nav-icon-wrap">
                        <Icon className="i" size={17} />
                        {badge > 0 && (
                          <span
                            className="nav-badge"
                            aria-label={`${badge} pending`}
                            data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >{badge > 99 ? '99+' : badge}</span>
                        )}
                      </span>
                      {item.label}
                    </Link>
                  );
                })}
              </React.Fragment>
            );
          })}

          {!impersonating && <OnboardingChecklist profile={effectiveProfile} />}

          <UserChip profile={effectiveProfile} email={sessionEmail} />
          <Link
            to="/account"
            className="signout"
            style={{ background: 'transparent', color: 'var(--mute)', marginBottom: 4 }}
            data-testid="nav-account"
          >
            <User size={15} /> My account
          </Link>
          <button className="signout" onClick={handleSignOut} data-testid="signout-btn">
            <LogOut size={15} /> Sign out
          </button>

          {/* Mobile-only — desktop is served by UserChip + Sign out above.
              This tab is `display: none` on desktop and slots into the
              bottom-nav strip on ≤ 720px viewports. */}
          <MobileAccountSheet
            profile={effectiveProfile}
            email={sessionEmail}
            onSignOut={handleSignOut}
            active={mobileMenuOpen}
            onOpen={() => setMobileMenuOpen(true)}
            onClose={() => setMobileMenuOpen(false)}
          />
        </aside>

        <ShellContext.Provider
          value={{
            profile: effectiveProfile,
            realProfile: profile,
            isImpersonating: impersonating,
            exitImpersonation,
          }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {impersonating && (
              <div className="impersonation-banner" data-testid="impersonation-banner">
                <ShieldOff size={15} />
                <span>
                  Viewing as <b>{actAsShop?.name || '—'}</b>{' '}
                  <span style={{ opacity: 0.8 }}>· acting on behalf of the shop</span>
                </span>
                <button
                  type="button"
                  className="btn s"
                  onClick={exitImpersonation}
                  data-testid="impersonation-exit"
                >
                  <ArrowLeft size={13} /> Exit
                </button>
              </div>
            )}
            {children}
          </div>
        </ShellContext.Provider>
      </div>
    </div>
  );
};

export const ShellContext = React.createContext({
  profile: null, realProfile: null, isImpersonating: false, exitImpersonation: () => {},
});

export const useShell = () => React.useContext(ShellContext);
